/**
 * Employee duplicate merge (4.36.1, Part A — admin only).
 *
 * Pick a survivor and a duplicate, preview exactly what will move (with counts
 * and masked sensitive values), choose which record's value to keep per field,
 * then type the survivor's number to confirm. Nothing is deleted: the duplicate
 * becomes read-only and merged into the survivor.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { employeesAPI, employeeMergeAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { GitMerge, AlertTriangle, ShieldAlert, ArrowRight, Lock } from 'lucide-react';

const FIELD_LABELS = {
  first_name: 'First name', last_name: 'Last name', middle_name: 'Middle name',
  preferred_name: 'Preferred name', suffix: 'Suffix', email: 'Email',
  phone_primary: 'Primary phone', phone_secondary: 'Secondary phone', address: 'Address',
  city: 'City', state: 'State', zip_code: 'ZIP', position: 'Position', company: 'Company',
  hire_date: 'Hire date', classification_description: 'Classification',
  department_id: 'Department', location_id: 'Location', date_of_birth: 'Date of birth', ssn: 'SSN',
};

function EmployeePicker({ label, employees, value, onChange, excludeId, testid }) {
  const [q, setQ] = useState('');
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return employees.filter((e) => e.id !== excludeId && (
      `${e.first_name} ${e.last_name}`.toLowerCase().includes(t) ||
      String(e.employee_number || '').toLowerCase().includes(t)
    )).slice(0, 8);
  }, [q, employees, excludeId]);
  const selected = employees.find((e) => e.id === value);
  return (
    <div className="space-y-2" data-testid={testid}>
      <Label>{label}</Label>
      {selected ? (
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="font-semibold">{selected.first_name} {selected.last_name}</p>
            <p className="text-sm text-slate-500">#{selected.employee_number || '—'}{selected.is_active === false ? ' · inactive' : ''}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { onChange(null); setQ(''); }} data-testid={`${testid}-clear`}>Change</Button>
        </div>
      ) : (
        <div className="relative">
          <Input placeholder="Search name or number…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-input`} />
          {matches.length > 0 && (
            <div className="absolute z-10 mt-1 w-full rounded-lg border bg-popover text-popover-foreground shadow-lg">
              {matches.map((e) => (
                <button key={e.id} type="button" onClick={() => { onChange(e.id); setQ(''); }}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                  data-testid={`${testid}-option-${e.id}`}>
                  <span>{e.first_name} {e.last_name}</span>
                  <span className="text-muted-foreground">#{e.employee_number || '—'}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EmployeeMerge() {
  const [employees, setEmployees] = useState([]);
  const [survivorId, setSurvivorId] = useState(null);
  const [loserId, setLoserId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [choices, setChoices] = useState({});
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    employeesAPI.list().then(({ data }) => setEmployees(data)).catch(() => toast.error('Could not load employees'));
  }, []);

  const runPreview = useCallback(async () => {
    if (!survivorId || !loserId) return;
    setLoading(true); setPreview(null); setChoices({}); setConfirmText('');
    try {
      const { data } = await employeeMergeAPI.preview(survivorId, loserId);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not build the merge preview');
    } finally { setLoading(false); }
  }, [survivorId, loserId]);

  const apply = useCallback(async () => {
    setApplying(true);
    try {
      const { data } = await employeeMergeAPI.apply({
        survivor_id: survivorId, loser_id: loserId,
        field_choices: choices, typed_confirmation: confirmText.trim(),
      });
      const movedTotal = Object.values(data.records_moved || {}).reduce((a, b) => a + b, 0);
      toast.success(`Merged. ${movedTotal} record(s) moved to the survivor.`);
      setPreview(null); setSurvivorId(null); setLoserId(null); setConfirmText('');
      employeesAPI.list().then(({ data: d }) => setEmployees(d));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Merge failed');
    } finally { setApplying(false); }
  }, [survivorId, loserId, choices, confirmText]);

  const confirmOk = preview && confirmText.trim() === preview.confirmation_phrase;
  const execCovered = preview && (preview.exec_covered?.survivor || preview.exec_covered?.loser);

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="employee-merge-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold"><GitMerge className="h-6 w-6" />Merge duplicate employees</h1>
        <p className="text-sm text-slate-500">Nothing is deleted — the duplicate becomes read-only and is kept, linked to the survivor you keep.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Choose the two records</CardTitle>
          <CardDescription>The survivor is the record you keep. The duplicate is merged into it.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <EmployeePicker label="Survivor (keep this one)" employees={employees} value={survivorId} onChange={setSurvivorId} excludeId={loserId} testid="survivor-picker" />
            <EmployeePicker label="Duplicate (merge away)" employees={employees} value={loserId} onChange={setLoserId} excludeId={survivorId} testid="loser-picker" />
          </div>
          <Button onClick={runPreview} disabled={!survivorId || !loserId || loading} data-testid="preview-merge-btn">
            {loading ? 'Building preview…' : 'Preview merge'}
          </Button>
        </CardContent>
      </Card>

      {preview && (
        <>
          {execCovered && (
            <div className="flex items-start gap-3 rounded-lg border border-purple-300 bg-purple-50 p-4 dark:bg-purple-950/30" data-testid="exec-comp-note">
              <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-purple-600" />
              <p className="text-sm text-purple-800 dark:text-purple-300">One of these records is flagged <strong>Executive Comp</strong>. You have the Executive Compensation grant, so you can complete this merge — please take extra care.</p>
            </div>
          )}

          {preview.winteam_conflict?.present && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:bg-amber-950/30" data-testid="winteam-conflict-note">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <p className="text-sm text-amber-800 dark:text-amber-300">{preview.winteam_conflict.message}</p>
            </div>
          )}

          <Card data-testid="merge-side-by-side">
            <CardHeader>
              <CardTitle className="text-lg">Pick which value to keep</CardTitle>
              <CardDescription>Sensitive fields show only the last 4 / on-file — never the full value.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 gap-y-1 text-sm">
                <div className="font-semibold text-slate-500">Field</div>
                <div className="px-3 text-center font-semibold">{preview.survivor.name}<div className="text-xs font-normal text-slate-400">survivor</div></div>
                <div className="px-3 text-center font-semibold">{preview.loser.name}<div className="text-xs font-normal text-slate-400">duplicate</div></div>
                {preview.fields.filter((f) => f.differs).map((f) => {
                  const chosen = choices[f.field] || 'survivor';
                  return (
                    <React.Fragment key={f.field}>
                      <div className="border-t py-2">{FIELD_LABELS[f.field] || f.field}{f.sensitive && <Lock className="ml-1 inline h-3 w-3 text-slate-400" />}</div>
                      <button type="button" onClick={() => setChoices((c) => ({ ...c, [f.field]: 'survivor' }))}
                        className={`border-t px-3 py-2 text-center ${chosen === 'survivor' ? 'bg-blue-50 font-semibold text-blue-700 dark:bg-blue-950/40' : 'text-slate-500'}`}
                        data-testid={`choose-survivor-${f.field}`}>{f.survivor}</button>
                      <button type="button" onClick={() => setChoices((c) => ({ ...c, [f.field]: 'loser' }))}
                        className={`border-t px-3 py-2 text-center ${chosen === 'loser' ? 'bg-blue-50 font-semibold text-blue-700 dark:bg-blue-950/40' : 'text-slate-500'}`}
                        data-testid={`choose-loser-${f.field}`}>{f.loser}</button>
                    </React.Fragment>
                  );
                })}
                {preview.fields.filter((f) => f.differs).length === 0 && (
                  <div className="col-span-3 border-t py-3 text-center text-slate-400">The two records have identical field values.</div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="merge-move-summary">
            <CardHeader>
              <CardTitle className="text-lg">What will move to the survivor</CardTitle>
              <CardDescription>Everything attached to the duplicate is re-pointed to the survivor. History stays and is masked.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(preview.move_summary).map(([k, v]) => (
                  <Badge key={k} variant={v > 0 ? 'default' : 'secondary'} className="capitalize" data-testid={`move-${k}`}>
                    {k.replace(/_/g, ' ')}: {v}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 pt-6">
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <span className="font-semibold">{preview.loser.name}</span><ArrowRight className="h-4 w-4" /><span className="font-semibold">{preview.survivor.name}</span>
              </div>
              <Label htmlFor="merge-confirm">Type the survivor's number <strong>{preview.confirmation_phrase}</strong> to confirm</Label>
              <Input id="merge-confirm" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                placeholder={preview.confirmation_phrase} data-testid="merge-confirm-input" />
              <Button onClick={apply} disabled={!confirmOk || applying} variant="destructive" data-testid="confirm-merge-btn">
                {applying ? 'Merging…' : 'Merge — this cannot be undone'}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
